import { useEffect, useState } from "react";
import { MachineCollection } from "../../../Types";
import { ContentProfile } from "../../../api/admin";

/**
 * Owned by ContentProfileDetail, not SettingsTab, so an in-progress edit
 * survives switching to the Devices tab and back — SettingsTab unmounts on
 * tab switch (same conditional-render pattern as ClockConfiguration.tsx),
 * and local state doesn't survive that.
 */
// profile may still be undefined while ContentProfileDetail's own
// useContentProfile query is loading — this hook has to be called
// unconditionally (before that query's loading/error early returns), so it
// tolerates that and fills in once the profile arrives.
export const useProfileDraft = (profile: ContentProfile | undefined) => {
  const [values, setValues] = useState<Record<string, unknown>>(
    profile?.values ?? {},
  );
  const [machineCollections, setMachineCollections] = useState<
    MachineCollection[]
  >(profile?.machineCollections ?? []);
  const [favorites, setFavorites] = useState<string[]>(
    profile?.favorites ?? [],
  );
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setValues(profile.values);
    setMachineCollections(profile.machineCollections);
    setFavorites(profile.favorites);
    setDirty(false);
  }, [profile?.id]);

  const setField = (field: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const setMachineCollectionsDraft = (next: MachineCollection[]) => {
    setMachineCollections(next);
    setDirty(true);
  };

  const setFavoritesDraft = (next: string[]) => {
    setFavorites(next);
    setDirty(true);
  };

  // "Not managed": the key leaves `values`, so Sync stops pushing it and
  // each device keeps whatever it has.
  const unsetField = (field: string) => {
    setValues((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setDirty(true);
  };

  const markSaved = () => setDirty(false);

  return {
    values,
    machineCollections,
    favorites,
    dirty,
    setField,
    unsetField,
    setMachineCollections: setMachineCollectionsDraft,
    setFavorites: setFavoritesDraft,
    markSaved,
  };
};

export type ProfileDraft = ReturnType<typeof useProfileDraft>;
